import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { setRankCatalog } from "../lib/ranks";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";

const MetaContext = createContext({ ranks: [], titles: [], features: { lineage_trees: true, geo_rooms: false, themed_rooms: true }, reload: () => {}, canAnnounce: false });

export function MetaProvider({ children }) {
  const { user } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [titles, setTitles] = useState([]);
  const [features, setFeatures] = useState({ lineage_trees: true, geo_rooms: false, themed_rooms: true });
  const [version, setVersion] = useState(0);

  const reload = useCallback(async () => {
    const [r, t, f] = await Promise.all([api.get("/ranks"), api.get("/titles"), api.get("/features").catch(() => ({ data: { lineage_trees: true, geo_rooms: false, themed_rooms: true } }))]);
    setRankCatalog(r.data);
    setRanks(r.data);
    setTitles(t.data);
    setFeatures(f.data || { lineage_trees: true, geo_rooms: false, themed_rooms: true });
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  // Alpha flipped a site-wide flag (e.g. hide titles) — apply it live, no refresh needed.
  const { subscribe } = useSocket() || {};
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((ev) => { if (ev?.type === "features_updated" && ev.features) setFeatures(ev.features); });
  }, [subscribe]);

  const canAnnounce = useMemo(() => {
    const r = ranks.find((x) => x.name === user?.rank);
    return !!(r && r.can_announce);
  }, [ranks, user]);

  const value = useMemo(
    () => ({ ranks, titles, features, reload, version, canAnnounce }),
    [ranks, titles, features, reload, version, canAnnounce]
  );

  return (
    <MetaContext.Provider value={value}>
      {children}
    </MetaContext.Provider>
  );
}

export const useMeta = () => useContext(MetaContext);

export const useTitlesHidden = () => !!useContext(MetaContext).features?.titles_hidden;
